// private properties
const
    _inViewInstance = Symbol(),
    _inViewport = Symbol(),

    /**
     * copied from in-view package due to issues with the loaders
     * @see node_modules/in-view/src/viewport.js
     */
    inViewport = (element, options) => {
        const { top, right, bottom, left, width, height } = element.getBoundingClientRect(),
            intersection = {
                t: bottom,
                r: window.innerWidth - left,
                b: window.innerHeight - top,
                l: right,
            },
            threshold = {
                x: options.threshold * width,
                y: options.threshold * height,
            };

        return intersection.t > (options.offset.top    + threshold.y)
            && intersection.r > (options.offset.right  + threshold.x)
            && intersection.b > (options.offset.bottom + threshold.y)
            && intersection.l > (options.offset.left   + threshold.x);

    };

/**
 * @typedef {Object} ScrollNavOptions
 * @property {int|string|function} offset The offset - or an element to measure the offset from, or a callback returning the offset - to apply to scrolling. Defaults to <code>'.header'</code>
 * @property {string} attrName The name of the attribute to select elements to watch and eventually get the new hash from. Defaults to <code>'data-scrollnav-anchor'</code>.
 * @property {string} stateSelector The selector to get links that should be marked as active if their <code>href</code>-attribute matches the id of the active element. Defaults to <code>'.js-scrollnav-state-link'</code>.
 * @property {string} scrollTopSelector The selector to get the 'scroll-top'-target element. Defaults to <code>'#scroll-top'</code>.
 * @property {boolean} scrollFromHashChanges Whether updates of the hash should trigger scrollings. Defaults to <code>false</code>.
 * @property {string} defaultHash The hash to set, when the page is scrolled to top (e.g. element defined by parameter <code>scrollTopSelector</code> is visible).
 * @property {function(HTMLElement):boolean} test  An optional function for custom determining if the passed element is 'in view'.
 * @property {function(HTMLElement):string} getHash An optional function returning the hash to set to the url. Receives the current element.
 * @property {function(string):string} getAttribute An optional function returning the value of the attribute defined by <code>attrName</code>, matching the element to scroll to from hash changes. Receives the current hash.
 * @property {function(string):string} getActiveStateSelector An optional function returning the selector for the active element defined by <code>stateSelector</code>. Receives the current hash. Defaults to <code>`[href="${hash}"]`</code>
 * @property {function(HTMLElement, string)} onEnter An optional callback function, that is called after the hash has been updated. Receives the current element and the hash.
 */

/**
 * Updates the hash of the url when an element with attribute defined by parameter <code>attrName</code> is in view.
 * The new hash can be set by parameter <code>getHash</code>. If this parameter is omitted, the hash is determined by the content of the attribute defined by parameter <code>attrName</code> or by the id of the element, if the attribute is empty.
 * An element is 'in view' if a function passed to parameter <code>test</code> returns <code>true</code>. If this parameter is omitted, an element is considered 'in view', if its top is above the center of the screen and its bottom is below it.
 * If two or more elements are 'in view' in the same time, the last encountered in the DOM 'wins'.
 */
export default class ScrollNav {
    /**
     * @param {ScrollNavOptions} o Initial properties
     */
    constructor(o) {
        this.defaultOptions = {
            offset: '.header',
            attrName: 'data-scrollnav-anchor',
            stateSelector: '.js-scrollnav-state-link',
            scrollTopSelector: '#scroll-top',
            scrollFromHashChanges: false,
            defaultHash: '',
            test: undefined,
            getHash: undefined,
            getAttribute: undefined,
            getActiveStateSelector: undefined,
            onEnter: undefined,
        };

        this.options = $.extend({}, this.defaultOptions, o);

        const self = this,
            options = this.options,
            $win = $(window),
            $stateLinks = $(options.stateSelector),
            attrSelector = `[${options.attrName}]`;

        if (typeof options.offset !== 'number' && typeof options.offset !== 'function') {
            this.options.$offset = $(options.offset);
        }

        require(['in-view'], inView => {
            self[_inViewport] = inViewport;

            inView.test(typeof options.test === 'function' ? options.test : self.test.bind(self));

            self[_inViewInstance] = inView(`${attrSelector}, ${options.scrollTopSelector}`)
                .on('enter', el => {
                    let hash = options.defaultHash,
                        activeClass = 'is-active',
                        $stateLink, activeStateSelector,
                        isScrollTop = $(el).is(options.scrollTopSelector);

                    if (typeof options.getHash === 'function') {
                        hash = options.getHash(el);
                    } else {
                        hash = el.getAttribute(options.attrName) || el.id;
                    }

                    if (!isScrollTop && location.hash.indexOf('#' + hash) === -1) {
                        history.replaceState(null, null, '#' + hash);
                    } else if (isScrollTop) {
                        history.replaceState(null, null, location.pathname + location.search);
                    }

                    if (typeof options.getActiveStateSelector === 'function') {
                        activeStateSelector = options.getActiveStateSelector(hash);
                    } else {
                        activeStateSelector = `[href="#${hash}"]`;
                    }

                    $stateLink = $stateLinks.filter(activeStateSelector);

                    if ($stateLink.length) {
                        $stateLinks.parent().removeClass(activeClass);
                        $stateLink.parent().addClass(activeClass);
                    } else if (isScrollTop) {
                        $stateLinks.parent().removeClass(activeClass);
                    }

                    if (typeof onEnter === 'function') {
                        options.onEnter(el, hash);
                    }
                });

            if (options.scrollFromHashChanges === true) {
                // define function as variable, as function-definition inside if statements is not allowed in strict mode
                let onUrlChange = (event, initial) => {
                    let hash,
                        selector,
                        $target;

                    if (event) {
                        event.preventDefault();
                        event.stopImmediatePropagation();
                    }

                    if (typeof options.getAttribute === 'function') {
                        hash = options.getAttribute(location.hash);
                    } else {
                        hash = location.hash.substr(1);
                    }

                    selector = `[${options.attrName}="${hash}"]`;

                    try {
                        $target = $(selector);
                    } catch (e) {
                        return;
                    }

                    if (!$target.length) {
                        // support for normal anchors
                        try {
                            selector = '#' + hash;
                            $target = $(selector);
                        } catch (e) {
                            return;
                        }
                    }

                    if (!$target.length) {
                        return;
                    }

                    if (location.hash === options.scrollTopSelector) {
                        history.replaceState(null, null, location.pathname + location.search);
                    }

                    function scroll() {
                        let offset = 80;

                        if ('$offset' in options) {
                            offset += options.$offset.height();
                        } else if (typeof options.offset === 'function') {
                            offset = options.offset();
                        } else if (options.offset) {
                            offset = options.offset;
                        }

                        Foundation.SmoothScroll.scrollToLoc(selector, {
                            threshold: 0,
                            offset,
                            animationDuration: initial ? 0 : 300,
                        });
                    }

                    if (!$win.data('initiallyResized')) {
                        $(window).one('headroom.resized', () => {
                            scroll();
                        });
                    } else {
                        scroll();
                    }
                };

                // IE needs two separate handler
                $win.on('hashchange', onUrlChange)
                    .on('popstate', onUrlChange);
            }

            setTimeout(() => {
                if (CONFIG.initialHash) {
                    location.hash = CONFIG.initialHash.replace(/^#\/?(.*)/, '#/$1');
                } else {
                    self[_inViewInstance].check();
                }

            }, 1);
        });
    }

    defaultInViewTest(el) {
        return this[_inViewport](el, this[_inViewInstance].options);
    }

    test(el) {
        const $win = $(window),
            $el = $(el),
            scrollDelta = Math.max(document.body.scrollTop, document.documentElement.scrollTop),
            elOffset = $el.offset().top;

        if ($el.is(this.options.scrollTopSelector)) {
            return scrollDelta <= $el.height();
        }

        if (!$(el).is(`[${this.options.attrName}]`)) {
            // use default test, if the checked element does not match any of ours
            return this.defaultInViewTest(el);
        }

        return elOffset <= scrollDelta + $win.height() / 2 // the top of it is above the middle of the screen
            && elOffset + $el.height() > scrollDelta + $win.height() / 2; // and the bottom is below the the middle of the screen
    }
}



// WEBPACK FOOTER //
// ./resources/assets/js/lib/scroll-nav.js